import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { toPageError } from '../partners/PartnerListPage';
import { defaultWithdrawalListQuery, withdrawalsRepositoryToken } from './repository';
import type {
  WithdrawalListFilters,
  WithdrawalListQuery,
  WithdrawalDeliveryResult,
  WithdrawalStatus,
  WithdrawalSummary,
} from './types';
import {
  formatReservedAge,
  formatWithdrawalMoney,
  maskWithdrawalIdentifier,
  safeMaskedDiagnostic,
} from './types';

export function WithdrawalListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(withdrawalsRepositoryToken);
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [query, setQuery] = useState<WithdrawalListQuery>(() => initialQuery(searchParams));
  const [pageIndex, setPageIndex] = useState(0);
  const [cursorHistory, setCursorHistory] = useState<readonly string[]>(['']);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refreshKey, repository],
    (page) => page.items.length === 0 && pageIndex === 0,
  );
  const columns = useMemo<readonly GridColDef<WithdrawalSummary>[]>(
    () => [
      {
        field: 'partnerWithdrawalId',
        headerName: t('withdrawals.fields.partnerWithdrawalId'),
        minWidth: 210,
        renderCell: ({ row }) => (
          <MuiLink component={Link} to={`${paths.affiliateWithdrawals}/${row.id}`}>
            {row.partnerWithdrawalId}
          </MuiLink>
        ),
      },
      { field: 'id', headerName: t('withdrawals.fields.internalId'), minWidth: 285 },
      { field: 'transferId', headerName: t('withdrawals.fields.transferId'), minWidth: 285 },
      {
        field: 'partnerCreditId',
        headerName: t('withdrawals.fields.partnerCreditId'),
        minWidth: 150,
        valueGetter: (_value, row) => maskWithdrawalIdentifier(row.partnerCreditId),
      },
      { field: 'username', headerName: t('withdrawals.fields.user'), minWidth: 150 },
      {
        field: 'partner',
        headerName: t('withdrawals.fields.partner'),
        minWidth: 140,
        valueGetter: (_value, row) => row.partner.code,
      },
      {
        field: 'agent',
        headerName: t('withdrawals.fields.agent'),
        minWidth: 140,
        valueGetter: (_value, row) => row.initiatingAgent?.code ?? t('common.notAvailable'),
      },
      {
        field: 'amount',
        headerName: t('withdrawals.fields.amount'),
        minWidth: 145,
        valueGetter: (_value, row) => formatWithdrawalMoney(row),
      },
      {
        field: 'status',
        headerName: t('withdrawals.fields.status'),
        minWidth: 210,
        renderCell: ({ row }) => <WithdrawalStatusChip status={row.status} />,
      },
      {
        field: 'exception',
        headerName: t('withdrawals.fields.exception'),
        minWidth: 220,
        sortable: false,
        valueGetter: (_value, row) =>
          [row.failureCode, row.latestDelivery?.normalizedResult].filter(Boolean).join(' · ') ||
          t('common.notAvailable'),
      },
      {
        field: 'reservedAge',
        headerName: t('withdrawals.fields.reservedAge'),
        minWidth: 130,
        sortable: false,
        valueGetter: (_value, row) => formatReservedAge(row.reservationAgeSeconds),
      },
      { field: 'attemptCount', headerName: t('withdrawals.fields.attempts'), minWidth: 100 },
      {
        field: 'lastResponse',
        headerName: t('withdrawals.fields.lastResponse'),
        minWidth: 250,
        sortable: false,
        valueGetter: (_value, row) => safeMaskedDiagnostic(row.latestDelivery?.errorDetail ?? null),
      },
      {
        field: 'createdAt',
        headerName: t('withdrawals.fields.createdAt'),
        minWidth: 205,
        valueFormatter: (value: string) => withOffset(value, language),
      },
      {
        field: 'completedAt',
        headerName: t('withdrawals.fields.completedAt'),
        minWidth: 205,
        sortable: false,
        valueFormatter: (value: string | null) =>
          value === null ? t('common.notAvailable') : withOffset(value, language),
      },
    ],
    [language, t],
  );

  function apply(filters: WithdrawalListFilters, sort: WithdrawalListQuery['sort']) {
    setPageIndex(0);
    setCursorHistory(['']);
    setQuery({ cursor: '', pageSize: query.pageSize, sort, filters: normalize(filters) });
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('withdrawals.subtitle')}
        title={t('withdrawals.title')}
      />
      <WithdrawalFiltersForm
        defaultFilters={defaultWithdrawalListQuery.filters}
        onApply={apply}
        value={query}
      />
      <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
        {t('withdrawals.serverOrderNote')}
      </Typography>
      <PageState
        emptyMessage={t('withdrawals.empty')}
        error={toPageError(resource.error, t('withdrawals.errors.list'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={1}>
            {mobile ? (
              <CompactWithdrawalResults language={language} withdrawals={resource.data.items} />
            ) : (
              <Box
                aria-label={t('withdrawals.results')}
                role="region"
                sx={{ maxWidth: '100%', overflowX: 'auto' }}
                tabIndex={0}
              >
                <Box sx={{ height: 570, minWidth: 2900 }}>
                  <DataGrid
                    aria-label={t('withdrawals.results')}
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter
                    rows={resource.data.items}
                    sortingMode="server"
                  />
                </Box>
              </Box>
            )}
            <CursorButtons
              nextCursor={resource.data.nextCursor}
              onNext={(cursor) => {
                const next = pageIndex + 1;
                setCursorHistory((current) => [...current.slice(0, next), cursor]);
                setPageIndex(next);
                setQuery((current) => ({ ...current, cursor }));
              }}
              onPrevious={() => {
                const previous = pageIndex - 1;
                setPageIndex(previous);
                setQuery((current) => ({ ...current, cursor: cursorHistory[previous] ?? '' }));
              }}
              previousDisabled={pageIndex === 0}
            />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function WithdrawalFiltersForm({
  defaultFilters,
  onApply,
  value,
}: {
  readonly defaultFilters: WithdrawalListFilters;
  readonly onApply: (filters: WithdrawalListFilters, sort: WithdrawalListQuery['sort']) => void;
  readonly value: WithdrawalListQuery;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset, watch } = useForm<
    WithdrawalListFilters & { readonly sort: WithdrawalListQuery['sort'] }
  >({ defaultValues: { ...value.filters, sort: value.sort } });
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      partnerId: value.filters.partnerId,
      agentId: value.filters.agentId,
      walletId: value.filters.walletId,
      memberId: value.filters.memberId,
      partnerUserId: value.filters.partnerUserId,
      transferId: value.filters.transferId,
      partnerCreditId: value.filters.partnerCreditId,
      failureCode: value.filters.failureCode,
      deliveryResult: value.filters.deliveryResult,
      minimumAmount: value.filters.minimumAmount,
      maximumAmount: value.filters.maximumAmount,
      createdFrom: value.filters.createdFrom,
      createdTo: value.filters.createdTo,
      completedFrom: value.filters.completedFrom,
      completedTo: value.filters.completedTo,
      sort: value.sort,
    },
    {
      partnerId: defaultFilters.partnerId,
      agentId: defaultFilters.agentId,
      walletId: defaultFilters.walletId,
      memberId: defaultFilters.memberId,
      partnerUserId: defaultFilters.partnerUserId,
      transferId: defaultFilters.transferId,
      partnerCreditId: defaultFilters.partnerCreditId,
      failureCode: defaultFilters.failureCode,
      deliveryResult: defaultFilters.deliveryResult,
      minimumAmount: defaultFilters.minimumAmount,
      maximumAmount: defaultFilters.maximumAmount,
      createdFrom: defaultFilters.createdFrom,
      createdTo: defaultFilters.createdTo,
      completedFrom: defaultFilters.completedFrom,
      completedTo: defaultFilters.completedTo,
      sort: defaultWithdrawalListQuery.sort,
    },
  );
  const secondaryTextFields: readonly [keyof WithdrawalListFilters, string][] = [
    ['partnerId', 'partnerId'],
    ['agentId', 'agentId'],
    ['walletId', 'walletId'],
    ['memberId', 'memberId'],
    ['partnerUserId', 'partnerUserId'],
    ['transferId', 'transferId'],
    ['partnerCreditId', 'partnerCreditId'],
    ['failureCode', 'failureCode'],
    ['minimumAmount', 'minimumAmount'],
    ['maximumAmount', 'maximumAmount'],
    ['createdFrom', 'createdFrom'],
    ['createdTo', 'createdTo'],
    ['completedFrom', 'completedFrom'],
    ['completedTo', 'completedTo'],
  ];
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      ariaLabel={t('filterPanel.label', { page: t('nav.withdrawals') })}
      onClear={() => {
        reset({ ...defaultFilters, sort: defaultWithdrawalListQuery.sort });
        onApply(defaultFilters, defaultWithdrawalListQuery.sort);
      }}
      onSubmit={handleSubmit(({ sort, ...filters }) => onApply(filters, sort))}
      primaryFields={
        <>
          <TextField
            label={t('withdrawals.fields.partnerWithdrawalId')}
            size="small"
            {...register('partnerWithdrawalId')}
          />
          <TextField label={t('withdrawals.fields.user')} size="small" {...register('username')} />
          <TextField
            label={t('withdrawals.fields.status')}
            select
            size="small"
            value={watch('status')}
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {withdrawalStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('withdrawals.fields.currency')}
            size="small"
            {...register('currency')}
          />
        </>
      }
      secondaryFields={
        <>
          {secondaryTextFields.map(([field, label]) => (
            <TextField
              key={field}
              label={t(`withdrawals.fields.${label}`)}
              size="small"
              slotProps={
                field.endsWith('From') || field.endsWith('To')
                  ? { inputLabel: { shrink: true } }
                  : undefined
              }
              type={field.endsWith('From') || field.endsWith('To') ? 'datetime-local' : 'text'}
              {...register(field)}
            />
          ))}
          <TextField
            label={t('withdrawals.fields.deliveryResult')}
            select
            size="small"
            value={watch('deliveryResult')}
            {...register('deliveryResult')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {['CREDITED', 'NOT_CREDITED', 'RETRYABLE_FAILURE', 'UNKNOWN'].map((result) => (
              <MenuItem key={result} value={result}>
                {result}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('withdrawals.fields.sort')}
            select
            size="small"
            value={watch('sort')}
            {...register('sort')}
          >
            <MenuItem value="createdAt">{t('withdrawals.fields.createdAt')}</MenuItem>
            <MenuItem value="updatedAt">{t('withdrawals.fields.updatedAt')}</MenuItem>
          </TextField>
        </>
      }
    />
  );
}

function CompactWithdrawalResults({
  language,
  withdrawals,
}: {
  readonly language: TimeLanguage;
  readonly withdrawals: readonly WithdrawalSummary[];
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('withdrawals.results')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {withdrawals.map((item) => (
        <Paper component="li" key={item.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <WithdrawalStatusChip status={item.status} />
            <MuiLink component={Link} to={`${paths.affiliateWithdrawals}/${item.id}`}>
              {item.partnerWithdrawalId}
            </MuiLink>
            <Typography variant="body2">
              {item.partner.code} · {item.initiatingAgent?.code ?? t('common.notAvailable')} ·{' '}
              {item.username}
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>{formatWithdrawalMoney(item)}</Typography>
            <Typography variant="body2">
              {t('withdrawals.fields.reservedAge')}: {formatReservedAge(item.reservationAgeSeconds)}
            </Typography>
            <Typography variant="body2">
              {item.failureCode ?? t('common.notAvailable')} ·{' '}
              {safeMaskedDiagnostic(item.latestDelivery?.errorDetail ?? null)}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {withOffset(item.createdAt, language)}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

export function WithdrawalStatusChip({ status }: { readonly status: WithdrawalStatus }) {
  const color =
    status === 'CREDIT_STATUS_UNKNOWN' || status === 'MANUAL_REVIEW'
      ? 'error'
      : status === 'RETRY_PENDING' || status === 'CREDITING' || status === 'RESERVED'
        ? 'warning'
        : status === 'COMPLETED'
          ? 'success'
          : 'default';
  return <Chip color={color} label={status} size="small" />;
}

export function CursorButtons({
  nextCursor,
  onNext,
  onPrevious,
  previousDisabled,
}: {
  readonly nextCursor: string | null;
  readonly onNext: (cursor: string) => void;
  readonly onPrevious: () => void;
  readonly previousDisabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      <Button disabled={previousDisabled} onClick={onPrevious}>
        {t('withdrawals.pagination.previous')}
      </Button>
      <Button
        disabled={nextCursor === null}
        onClick={() => {
          if (nextCursor !== null) onNext(nextCursor);
        }}
      >
        {t('withdrawals.pagination.next')}
      </Button>
    </Stack>
  );
}

const withdrawalStatuses: readonly WithdrawalStatus[] = [
  'REQUESTED',
  'RESERVED',
  'CREDITING',
  'RETRY_PENDING',
  'CREDIT_STATUS_UNKNOWN',
  'MANUAL_REVIEW',
  'COMPLETED',
  'FAILED_RELEASED',
  'REJECTED',
];
const withdrawalDeliveryResults: readonly NonNullable<WithdrawalDeliveryResult>[] = [
  'CREDITED',
  'NOT_CREDITED',
  'RETRYABLE_FAILURE',
  'UNKNOWN',
];
function normalize(filters: WithdrawalListFilters): WithdrawalListFilters {
  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ]),
  ) as unknown as WithdrawalListFilters;
}

function initialQuery(searchParams: URLSearchParams): WithdrawalListQuery {
  const text = (key: keyof WithdrawalListFilters) => searchParams.get(key)?.trim() ?? '';
  const statusValue = text('status');
  const deliveryResultValue = text('deliveryResult');
  const sortValue = searchParams.get('sort')?.trim();
  return {
    ...defaultWithdrawalListQuery,
    sort: sortValue === 'updatedAt' ? 'updatedAt' : 'createdAt',
    filters: {
      partnerId: text('partnerId'),
      agentId: text('agentId'),
      walletId: text('walletId'),
      memberId: text('memberId'),
      username: text('username'),
      partnerUserId: text('partnerUserId'),
      partnerWithdrawalId: text('partnerWithdrawalId'),
      transferId: text('transferId'),
      partnerCreditId: text('partnerCreditId'),
      currency: text('currency'),
      status: withdrawalStatuses.includes(statusValue as WithdrawalStatus)
        ? (statusValue as WithdrawalStatus)
        : '',
      failureCode: text('failureCode'),
      deliveryResult: withdrawalDeliveryResults.includes(
        deliveryResultValue as NonNullable<WithdrawalDeliveryResult>,
      )
        ? (deliveryResultValue as NonNullable<WithdrawalDeliveryResult>)
        : '',
      minimumAmount: text('minimumAmount'),
      maximumAmount: text('maximumAmount'),
      createdFrom: text('createdFrom'),
      createdTo: text('createdTo'),
      completedFrom: text('completedFrom'),
      completedTo: text('completedTo'),
    },
  };
}
function withOffset(value: string, language: TimeLanguage) {
  const time = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${time.display} (${time.originalOffset})`;
}
